import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { matchesAPI, teamsAPI } from '../services/api';
import { getLogoUrl } from '../utils/logoUrl';
import {
  FaFutbol, FaTrophy, FaUsers, FaCalendar, FaChevronLeft, FaChevronRight,
  FaStar, FaFire, FaInfoCircle, FaGavel, FaFileAlt, FaUserTie,
  FaClipboardList, FaExclamationTriangle
} from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import ResultsDisplay from '../components/ResultsDisplay';
import SingleMatchResult from '../components/SingleMatchResult';
import { useLanguage } from '../context/LanguageContext';
import { contentAPI } from '../services/api';

const HomePage = () => {
  const { language, t } = useLanguage();
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [standings, setStandings] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [groupSlideIndex, setGroupSlideIndex] = useState(0);
  const [selectedHomeRound, setSelectedHomeRound] = useState('all');
  const [visibleRoundsCount, setVisibleRoundsCount] = useState(5);
  const [tickerData, setTickerData] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animateStats, setAnimateStats] = useState(false);

  const slides = [
    {
      title: language === 'ar' ? 'الرابطة الوطنية لكرة القدم للهواة' : 'Ligue Nationale de Football Amateur',
      subtitle: language === 'ar' ? 'المستوى الثاني - الموسم 2025/2026' : 'Niveau 2 - Saison 2025/2026',
      description: language === 'ar' ? 'نحن نصنع مستقبل كرة القدم التونسية من خلال دعم المواهب والأندية الهاوية' : 'Nous bâtissons l\'avenir du football tunisien en soutenant les talents et les clubs amateurs',
      image: 'https://images.unsplash.com/photo-1459865264687-595d652de67e?w=1920&q=80'
    },
    {
      title: language === 'ar' ? 'تغطية شاملة ومباشرة' : 'Couverture Complète et en Direct',
      subtitle: language === 'ar' ? 'كل المعلومات في مكان واحد' : 'Toutes les informations en un seul endroit',
      description: language === 'ar' ? 'تابع النتائج الحينية، الترتيب، وتعيينات الحكام والقرارات التأديبية لحظة بلحظة' : 'Suivez les résultats en direct, le classement, les désignations d\'arbitres et les décisions disciplinaires',
      image: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=1920&q=80'
    },
    {
      title: language === 'ar' ? 'روح رياضية ومنافسة شريفة' : 'Esprit Sportif et Compétition Loyale',
      subtitle: language === 'ar' ? 'دعم الأندية الرياضية' : 'Soutien aux clubs sportifs',
      description: language === 'ar' ? 'نعمل على تطوير البنية التحتية والرفع من مستوى التنافس بين جميع الفرق المشاركة' : 'Nous oeuvrons pour le développement des infrastructures et l\'élévation du niveau de compétition',
      image: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1920&q=80'
    }
  ];

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);

    const groupInterval = setInterval(() => {
      setGroupSlideIndex((prev) => (prev + 1) % 6);
    }, 5000);

    const handleScroll = () => {
      const statsSection = document.getElementById('stats-section');
      if (statsSection) {
        const rect = statsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom >= 0) {
          setAnimateStats(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    // Supprimer le filigrane uniquement sur l'accueil
    document.body.classList.add('no-watermark');

    return () => {
      clearInterval(interval);
      clearInterval(groupInterval);
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove('no-watermark');
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [matchesRes, teamsRes, standingsRes, tickerRes, newsRes] = await Promise.all([
        matchesAPI.getAll(),
        teamsAPI.getAll(),
        matchesAPI.getStandings(),
        contentAPI.getTicker(),
        contentAPI.getClubNews()
      ]);
      setMatches(matchesRes.data || matchesRes);
      const teamsData = teamsRes.data || teamsRes;
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setStandings((standingsRes.data || standingsRes).slice(0, 5));
      setTickerData(tickerRes.data || tickerRes || []);
      setNews((newsRes.data || newsRes || []).slice(0, 3));
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const finishedRounds = [...new Set(matches.filter(m => m.status === 'finished').map(m => m.round))]
    .filter(r => r)
    .sort((a, b) => b - a);
  const latestRound = finishedRounds[0] || 1;

  // Suppression du useEffect qui forçait la journée la plus récente

  if (loading) return <LoadingSpinner />;

  const sortedFinished = [...matches]
    .filter(m => m.status === 'finished')
    .sort((a, b) => (b.round || 0) - (a.round || 0) || new Date(b.match_date) - new Date(a.match_date));

  const sortedUpcoming = [...matches]
    .filter(m => m.status === 'upcoming')
    .sort((a, b) => (a.round || 0) - (b.round || 0) || new Date(a.match_date) - new Date(b.match_date));

  const latestMatches = sortedFinished.slice(0, 4);
  const upcomingMatches = sortedUpcoming.slice(0, 4);
  const totalMatches = matches.length;
  const totalGoals = sortedFinished
    .reduce((sum, m) => sum + (m.home_score || 0) + (m.away_score || 0), 0);

  const infoTiles = [
    { id: 1, label: t('about'), link: '/about', icon: FaInfoCircle, color1: '#4facfe', color2: '#00f2fe' },
    { id: 2, label: t('committees'), link: '/committees', icon: FaUserTie, color1: '#667eea', color2: '#764ba2' },
    { id: 3, label: t('regulations'), link: '/regulations', icon: FaFileAlt, color1: '#f093fb', color2: '#f5576c' },
    { id: 4, label: t('laws'), link: '/laws', icon: FaGavel, color1: '#84fab0', color2: '#8fd3f4' },
    { id: 5, label: t('designations'), link: '/designations', icon: FaClipboardList, color1: '#ff9a9e', color2: '#fecfef' },
    { id: 6, label: t('discipline'), link: '/discipline', icon: FaExclamationTriangle, color1: '#fa709a', color2: '#fee140' }
  ];

  return (
    <div style={styles.container}>
      {/* News Ticker - NEW */}
      {tickerData.length > 0 && (
        <div style={styles.tickerWrapper}>
          <div style={styles.tickerLabel}>{language === 'ar' ? 'آخر الأخبار' : 'Flash Info'}</div>
          <div className="ticker-scroll" style={styles.tickerScroll}>
            {tickerData.map((item, i) => (
              <span key={i} style={styles.tickerItem}>{item.text || item.content}</span>
            ))}
          </div>
        </div>
      )}

      {/* Premium Hero Slider with Club Logos Background */}
      <section className="home-hero-height" style={styles.heroSection}>
        {/* Animated Background Logos Container */}
        <div style={styles.logoBackground}>
          <div style={styles.logoGrid}>
            {[...teams, ...teams].map((team, i) => {
              const src = getLogoUrl(team.logo_url);
              return src ? (
                <img
                  key={i}
                  src={src}
                  style={styles.bgLogo}
                  alt=""
                  onError={(e) => e.target.style.display = 'none'}
                />
              ) : null;
            })}
          </div>
        </div>

        <div style={{
          ...styles.slide,
          backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url(${slides[currentSlide].image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'all 1.2s ease-in-out'
        }}>
          <div style={styles.slideOverlay}>
            <div style={styles.slideContent}>
              {(() => {
                const groupsByNumber = teams.reduce((acc, team) => {
                  const g = team.group_number != null ? Number(team.group_number) : 1;
                  const n = Math.min(6, Math.max(1, g));
                  if (!acc[n]) acc[n] = [];
                  acc[n].push(team);
                  return acc;
                }, {});
                const groupKeys = [1, 2, 3, 4, 5, 6].filter(k => groupsByNumber[k] && groupsByNumber[k].length > 0);
                const totalGroups = groupKeys.length || 1;
                const currentGroupNum = groupKeys[groupSlideIndex % totalGroups] || 1;
                const groupTeams = groupsByNumber[currentGroupNum] || [];
                const groupName = language === 'ar' ? `المجموعة ${currentGroupNum}` : `Groupe ${currentGroupNum}`;
                return (
                  <div style={styles.groupSliderWrap}>
                    <button
                      type="button"
                      aria-label={language === 'ar' ? 'السابق' : 'Précédent'}
                      style={{ ...styles.groupSliderBtn, left: 8 }}
                      onClick={() => setGroupSlideIndex((p) => (p - 1 + totalGroups) % totalGroups)}
                    >
                      <FaChevronLeft size={18} />
                    </button>
                    <div style={styles.groupSliderCard}>
                      <div style={styles.groupSliderTitle}>{groupName}</div>
                      <div style={styles.groupSliderLogos}>
                        {groupTeams.map((team, idx) => {
                          const logoSrc = getLogoUrl(team.logo_url);
                          return (
                            <div key={idx} style={styles.groupLogoBox} title={team.name || ''}>
                              {logoSrc ? (
                                <img src={logoSrc} style={styles.groupLogoImg} alt="" onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                              ) : null}
                              <FaFutbol size={14} color="rgba(255,255,255,0.5)" style={{ display: logoSrc ? 'none' : 'flex' }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label={language === 'ar' ? 'التالي' : 'Suivant'}
                      style={{ ...styles.groupSliderBtn, right: 8 }}
                      onClick={() => setGroupSlideIndex((p) => (p + 1) % totalGroups)}
                    >
                      <FaChevronRight size={18} />
                    </button>
                    <div style={styles.groupSliderDots}>
                      {groupKeys.map((_, i) => (
                        <button key={i} type="button" aria-label={`${language === 'ar' ? 'المجموعة' : 'Groupe'} ${i + 1}`} style={{ ...styles.groupDot, ...(i === groupSlideIndex % totalGroups ? styles.groupDotActive : {}) }} onClick={() => setGroupSlideIndex(i)} />
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div style={styles.badgeLabel}>{language === 'ar' ? 'الموسم الرياضي 2025/2026' : 'Saison Sportive 2025/2026'}</div>
              <h1 className="home-slide-title" style={styles.slideTitle}>{slides[currentSlide].title}</h1>
              <h2 className="home-slide-subtitle" style={styles.slideSubtitle}>{slides[currentSlide].subtitle}</h2>
              <div style={styles.divider}></div>
              <p className="home-slide-description" style={styles.slideDescription}>{slides[currentSlide].description}</p>

              <div style={styles.slideButtons}>
                <Link to="/live" style={styles.btnLive}>
                  <span style={styles.pulseDot}></span> {language === 'ar' ? 'متابعة البث المباشر' : 'Suivre le Live'}
                </Link>
                <Link to="/results" style={styles.btnSecondary}>{language === 'ar' ? 'نتائج الأسابيع' : 'Résultats Semaines'}</Link>
              </div>
            </div>
          </div>
        </div>

        <button onClick={prevSlide} style={{ ...styles.sliderBtn, left: '30px' }}>
          <FaChevronLeft />
        </button>
        <button onClick={nextSlide} style={{ ...styles.sliderBtn, right: '30px' }}>
          <FaChevronRight />
        </button>

        <div style={styles.sliderDots}>
          {slides.map((_, index) => (
            <span
              key={index}
              onClick={() => setCurrentSlide(index)}
              style={{
                ...styles.dot,
                width: currentSlide === index ? '30px' : '10px',
                background: currentSlide === index ? '#c41e3a' : 'rgba(255,255,255,0.5)'
              }}
            />
          ))}
        </div>
      </section>

      {/* Stats Section with Glassmorphism */}
      <section id="stats-section" className="home-stats-section" style={styles.statsSection}>
        <div className="home-stats-grid" style={styles.statsGrid}>
          {[
            { icon: FaUsers, val: teams.length, label: language === 'ar' ? 'فريق مشارك' : 'Équipes engagées' },
            { icon: FaFutbol, val: totalMatches, label: t('matches') },
            { icon: FaFire, val: totalGoals, label: language === 'ar' ? 'هدف مسجل' : 'Buts marqués', color: '#ff4d4d' },
            { icon: FaCalendar, val: '25/26', label: language === 'ar' ? 'الموسم الحالي' : 'Saison Actuelle' }
          ].map((stat, i) => (
            <div key={i} style={{
              ...styles.statCard,
              animation: animateStats ? `fadeInUp 0.6s ease forwards` : 'none',
              animationDelay: `${i * 0.15}s`
            }}>
              <stat.icon style={{ ...styles.statIcon, color: stat.color || '#c41e3a' }} />
              <h3 style={styles.statNumber}>{stat.val}</h3>
              <p style={styles.statLabel}>{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* League Values Section - NEW */}
      <section style={styles.valuesSection}>
        <div style={styles.valuesGrid}>
          {[
            {
              title: language === 'ar' ? 'النزاهة والروح الرياضية' : 'Intégrité et Esprit Sportif',
              desc: language === 'ar' ? 'نؤمن بالتنافس الشريف والاحترام المتبادل بين جميع الأطراف المتداخلة.' : 'Nous croyons en une compétition loyale et un respect mutuel entre toutes les parties.',
              icon: '🏆'
            },
            {
              title: language === 'ar' ? 'التطوير المستمر' : 'Développement Continu',
              desc: language === 'ar' ? 'نسعى لتطوير مهارات اللاعبين والكوادر الفنية والإدارية بانتظام.' : 'Nous œuvrons à développer régulièrement les compétences des joueurs et des cadres.',
              icon: '🚀'
            },
            {
              title: language === 'ar' ? 'الشفافية' : 'Transparence',
              desc: language === 'ar' ? 'التزام تام بالوضوح في اتخاذ القرارات ونشر المعلومات للجميع.' : 'Un engagement total envers la clarté dans la prise de décision et la diffusion de l\'information.',
              icon: '🤝'
            }
          ].map((val, i) => (
            <div key={i} style={styles.valueCard}>
              <div style={styles.valueIcon}>{val.icon}</div>
              <h3 style={styles.valueTitle}>{val.title}</h3>
              <p style={styles.valueDesc}>{val.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Info Tiles Section - NEW */}
      <section style={styles.tilesSection}>
        <div style={styles.tilesHeader}>
          <h2 style={styles.premiumTitle}>{language === 'ar' ? 'مساحة المعلومات' : 'Espace Informations'}</h2>
          <p style={styles.tilesSubtitle}>{language === 'ar' ? 'كل ما تود معرفته عن الرابطة والقوانين المنظمة' : 'Tout ce que vous devez savoir sur la ligue et les règlements'}</p>
        </div>
        <div className="home-tiles-grid" style={styles.tilesGrid}>
          {infoTiles.map((tile) => (
            <Link key={tile.id} to={tile.link} style={{
              ...styles.infoTile,
              background: `linear-gradient(135deg, ${tile.color1}, ${tile.color2})`
            }}>
              <div style={styles.tileIconBg}>
                <tile.icon style={styles.tileIcon} />
              </div>
              <h3 style={styles.tileLabel}>{tile.label}</h3>
              <div style={styles.tileArrow}>→</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="home-main-grid" style={styles.mainGridContent}>
        <div className="home-main-column" style={styles.mainColumn}>
          {/* All Results Section */}
          <section style={styles.cardSection}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.premiumTitle}>{t('results')}</h2>
              <div style={styles.roundSelector}>
                <select
                  value={selectedHomeRound}
                  onChange={(e) => setSelectedHomeRound(e.target.value)}
                  style={styles.homeRoundSelect}
                >
                  <option value="all">{language === 'ar' ? 'جميع الجولات' : 'Toutes les journées'}</option>
                  {finishedRounds.map(r => (
                    <option key={r} value={String(r)}>{t('round')} {r}</option>
                  ))}
                </select>
              </div>
            </div>
            {selectedHomeRound === 'all' ? (
              // Afficher le défilé des journées (limité au début)
              (() => {
                const finishedMatches = matches.filter(m => m.status === 'finished');
                const roundsToDisplay = finishedRounds.slice(0, visibleRoundsCount);

                if (finishedRounds.length === 0) return (
                  <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '15px', color: '#666' }}>
                    {t('noData')}
                  </div>
                );

                return (
                  <div style={styles.roundsListContainer}>
                    {roundsToDisplay.map(roundNum => (
                      <div key={roundNum} style={{ marginBottom: '40px' }}>
                        <div style={styles.roundLabelHome}>
                          <FaCalendar size={20} color="#c41e3a" />
                          <span>{t('round')} {roundNum}</span>
                        </div>
                        <ResultsDisplay
                          matches={finishedMatches.filter(m => String(m.round) === String(roundNum))}
                          allMatches={matches}
                          title={`${t('results')} ${t('round')} ${roundNum}`}
                          round={roundNum}
                        />
                      </div>
                    ))}
                    {visibleRoundsCount < finishedRounds.length && (
                      <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <button
                          onClick={() => setVisibleRoundsCount(prev => prev + 3)}
                          style={styles.loadMoreBtn}
                        >
                          {language === 'ar' ? 'عرض جولات إضافية' : 'Voir plus de journées'} ↓
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <div style={styles.roundLabelHome}>
                  <FaCalendar size={20} color="#c41e3a" />
                  <span>{t('round')} {selectedHomeRound}</span>
                </div>
                <ResultsDisplay
                  matches={matches.filter(m => String(m.round) === String(selectedHomeRound) && m.status === 'finished')}
                  allMatches={matches}
                  title={`${t('results')} ${t('round')} ${selectedHomeRound}`}
                  round={selectedHomeRound}
                />
              </div>
            )}
            <div style={{ marginTop: '40px', textAlign: 'center' }}>
              <Link to="/results" style={styles.viewAllBtnLarge}>
                {language === 'ar' ? 'عرض أرشيف جميع الجولات' : 'Voir l\'archive de toutes les journées'} →
              </Link>
            </div>
          </section>

          {/* Latest News / Communiques Section - NEW */}
          <section style={styles.cardSection}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.premiumTitle}>{language === 'ar' ? 'أخبار الرابطة' : 'Actualités de la Ligue'}</h2>
              <Link to="/news" style={styles.viewAllBtn}>{t('viewAll')}</Link>
            </div>
            <div style={styles.newsGrid}>
              {news.length > 0 ? (
                news.map((item, i) => (
                  <Link key={i} to={`/news/${item.id}`} style={styles.newsCard}>
                    <div style={styles.newsImageContainer}>
                      <img src={item.image_url || 'https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=400&q=80'} alt="" style={styles.newsImage} />
                      <div style={styles.newsDateBadge}>{new Date(item.created_at).toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR')}</div>
                    </div>
                    <div style={styles.newsContent}>
                      <h3 style={styles.newsTitle}>{item.title}</h3>
                      <p style={styles.newsSnippet}>{item.snippet || (item.content ? item.content.substring(0, 100) + '...' : '')}</p>
                    </div>
                  </Link>
                ))
              ) : (
                // MOCK data to fill the space
                [1, 2, 3].map(i => (
                  <div key={i} style={styles.newsCard}>
                    <div style={styles.newsImageContainer}>
                      <img src={`https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&q=80&sig=${i}`} alt="" style={styles.newsImage} />
                    </div>
                    <div style={styles.newsContent}>
                      <h3 style={styles.newsTitle}>{language === 'ar' ? `آخر أخبار البطولة والنشاط الرياضي ${i}` : `Dernière actualité de la ligue ${i}`}</h3>
                      <p style={styles.newsSnippet}>{language === 'ar' ? 'تغطية شاملة لأهم الأحداث والقرارات التي تم اتخاذها في الاجتماع الأخير للرابطة.' : 'Couverture complète des événements et décisions prises lors de la dernière réunion.'}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Featured Match */}
          {latestMatches.length > 0 && (
            <section style={styles.cardSection}>
              <h2 style={styles.premiumTitle}>{language === 'ar' ? 'مباراة بارزة' : 'Match à la une'}</h2>
              <SingleMatchResult match={latestMatches[0]} />
            </section>
          )}

          {/* Media Space Preview */}
          <section style={styles.cardSection}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.premiumTitle}>{t('media')}</h2>
              <Link to="/media" style={styles.viewAllBtn}>{t('viewAll')}</Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              {[
                'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&q=80',
                'https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=400&q=80',
                'https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=400&q=80'
              ].map((img, i) => (
                <div key={i} style={{ borderRadius: '15px', overflow: 'hidden', height: '150px', position: 'relative', cursor: 'pointer' }}>
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }} className="media-preview-img" />
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.3s' }} className="media-preview-overlay">
                    <span style={{ color: 'white', fontSize: '24px' }}>＋</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="home-sidebar-column" style={styles.sidebarColumn}>
          {/* Standings Widget */}
          <section style={styles.sidebarCard}>
            <div style={styles.sidebarHeader}>
              <h3 style={styles.sidebarTitle}>الترتيب العام</h3>
              <Link to="/standings" style={styles.sidebarLink}>التفاصيل</Link>
            </div>
            <div style={styles.miniStandings}>
              {standings.map((team, idx) => (
                <div key={team.id} style={styles.miniRow}>
                  <span style={styles.rank}>{idx + 1}</span>
                  {getLogoUrl(team.logo_url) ? (
                    <img src={getLogoUrl(team.logo_url)} style={styles.miniLogo} alt="" onError={(e) => e.target.style.display = 'none'} />
                  ) : (
                    <FaFutbol size={16} color="#c41e3a" style={{ marginRight: '8px' }} />
                  )}
                  <span style={styles.miniTeamName}>{team.name}</span>
                  <span style={styles.miniPoints}>{team.points} ن</span>
                </div>
              ))}
            </div>
          </section>

          {/* Next Matchday Schedule */}
          <section style={styles.sidebarCard}>
            <h3 style={styles.sidebarTitle}>المباريات القادمة</h3>
            <div style={styles.upcomingVList}>
              {upcomingMatches.map(match => (
                <div key={match.id} style={styles.vMatchItem}>
                  <div style={styles.vMatchTime}>{new Date(match.match_date).toLocaleTimeString('ar-TN', { hour: '2-digit', minute: '2-digit' })}</div>
                  <div style={styles.vMatchTeams}>
                    <span>{match.home_team_name}</span>
                    <span style={styles.vMatchVs}>vs</span>
                    <span>{match.away_team_name}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Partners Section - NEW */}
      <section style={styles.partnersSection}>
        <div style={styles.containerWidth}>
          <h2 style={styles.partnersTitle}>{language === 'ar' ? 'شركاؤنا' : 'Nos Partenaires'}</h2>
          <div className="partners-slider" style={styles.partnersGrid}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} style={styles.partnerLogoBox}>
                <span style={{ fontSize: '12px', color: '#999', fontWeight: 'bold' }}>PARTNER {i}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section - NEW */}
      <section style={styles.newsletterSection}>
        <div style={styles.newsletterCard}>
          <div style={styles.newsletterInfo}>
            <h2 style={styles.newsletterTitle}>{language === 'ar' ? 'اشترك في النشرة الإخبارية' : 'Abonnez-vous à la Newsletter'}</h2>
            <p style={styles.newsletterDesc}>{language === 'ar' ? 'كن أول من يعرف عن التغييرات، النتائج، والقرارات الهامة.' : 'Soyez le premier informé des changements, résultats et décisions importantes.'}</p>
          </div>
          <div style={styles.newsletterForm}>
            <input type="email" placeholder={language === 'ar' ? 'البريد الإلكتروني' : 'Votre Email'} style={styles.newsletterInput} />
            <button style={styles.newsletterBtn}>{language === 'ar' ? 'اشترك' : 'S\'abonner'}</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#fcfcfc',
    fontFamily: "'Cairo', sans-serif",
    width: '100%',
    maxWidth: '100vw',
    overflowX: 'hidden'
  },
  heroSection: {
    position: 'relative',
    minHeight: '400px',
    height: '750px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  },
  logoBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    opacity: 0.15,
    pointerEvents: 'none',
    overflow: 'hidden'
  },
  logoGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '28px',
    padding: '24px',
    animation: 'scrollLogos 60s linear infinite',
    width: '200%'
  },
  bgLogo: {
    width: '48px',
    height: '48px',
    objectFit: 'contain',
    filter: 'grayscale(100%)',
    opacity: 0.18
  },
  groupSliderWrap: {
    position: 'relative',
    margin: '0 auto 36px',
    maxWidth: '420px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  groupSliderBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.3)',
    background: 'rgba(0,0,0,0.2)',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  groupSliderCard: {
    background: 'rgba(255,255,255,0.07)',
    padding: '10px 14px',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.12)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
    minWidth: 0,
    flex: 1
  },
  groupSliderTitle: {
    fontSize: '11px',
    color: '#ff4d4d',
    fontWeight: '800',
    textTransform: 'uppercase',
    marginBottom: '8px',
    textAlign: 'center',
    letterSpacing: '0.5px'
  },
  groupSliderLogos: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap'
  },
  groupLogoBox: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.08)',
    borderRadius: '6px',
    flexShrink: 0
  },
  groupLogoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  groupSliderDots: {
    display: 'flex',
    gap: 6,
    marginTop: 8,
    justifyContent: 'center',
    position: 'absolute',
    bottom: -22,
    left: '50%',
    transform: 'translateX(-50%)'
  },
  groupDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.3)',
    cursor: 'pointer',
    padding: 0
  },
  groupDotActive: {
    background: '#ff4d4d',
    transform: 'scale(1.2)'
  },
  slide: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2
  },
  slideOverlay: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(circle, transparent 0%, rgba(196, 30, 58, 0.4) 100%)'
  },
  slideContent: {
    textAlign: 'center',
    color: 'white',
    padding: 'clamp(20px, 5vw, 40px)',
    maxWidth: 'min(900px, 95vw)',
    width: '100%',
    boxSizing: 'border-box',
    animation: 'fadeInScale 1.2s ease-out'
  },
  badgeLabel: {
    display: 'inline-block',
    padding: '6px 20px',
    background: 'rgba(196, 30, 58, 0.2)',
    border: '1px solid #c41e3a',
    color: '#ff4d4d',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '25px',
    letterSpacing: '1px'
  },
  featuredLogos: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '30px',
    animation: 'fadeInUp 1s ease-out'
  },
  featuredLogo: {
    width: '60px',
    height: '60px',
    objectFit: 'contain',
    filter: 'drop-shadow(0 5px 15px rgba(0,0,0,0.3))',
    transition: 'transform 0.3s ease'
  },
  slideTitle: {
    fontSize: 'clamp(22px, 5vw, 68px)',
    fontWeight: '900',
    marginBottom: '10px',
    lineHeight: '1.1',
    textShadow: '0 10px 30px rgba(0,0,0,0.5)'
  },
  slideSubtitle: {
    fontSize: 'clamp(16px, 2.5vw, 34px)',
    fontWeight: '300',
    marginBottom: '30px',
    opacity: 0.9
  },
  divider: {
    width: '80px',
    height: '4px',
    background: '#c41e3a',
    margin: '0 auto 30px'
  },
  slideDescription: {
    fontSize: 'clamp(14px, 2vw, 22px)',
    marginBottom: '45px',
    lineHeight: '1.6',
    fontWeight: '300',
    color: 'rgba(255,255,255,0.8)'
  },
  slideButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  btnLive: {
    padding: '18px 45px',
    background: '#c41e3a',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 15px 35px rgba(196, 30, 58, 0.4)',
    transition: 'all 0.3s'
  },
  pulseDot: {
    width: '10px',
    height: '10px',
    background: '#fff',
    borderRadius: '50%',
    animation: 'pulse 1.5s infinite'
  },
  btnSecondary: {
    padding: '18px 45px',
    background: 'rgba(255,255,255,0.1)',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: 'bold',
    border: '1px solid rgba(255,255,255,0.3)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s'
  },
  sliderBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '24px',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s',
    zIndex: 10,
    backdropFilter: 'blur(10px)'
  },
  sliderDots: {
    position: 'absolute',
    bottom: '40px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '12px',
    zIndex: 10
  },
  dot: {
    height: '10px',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
  },
  statsSection: {
    padding: 'clamp(40px, 8vw, 80px) clamp(16px, 4vw, 20px)',
    background: '#ffffff',
    marginTop: '-50px',
    position: 'relative',
    zIndex: 5,
    borderRadius: '30px 30px 0 0',
    boxShadow: '0 -20px 40px rgba(0,0,0,0.1)',
    width: '100%',
    boxSizing: 'border-box'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
    gap: '20px',
    maxWidth: '1300px',
    margin: '0 auto',
    width: '100%'
  },
  statCard: {
    textAlign: 'center',
    padding: '40px 20px',
    background: '#f8f9fa',
    borderRadius: '20px',
    border: '1px solid #eee',
    transition: 'all 0.3s ease',
    opacity: 0,
    transform: 'translateY(30px)'
  },
  statIcon: {
    fontSize: '48px',
    color: '#c41e3a',
    marginBottom: '20px'
  },
  statNumber: {
    fontSize: '44px',
    fontWeight: '900',
    color: '#1a1a1a',
    marginBottom: '8px'
  },
  statLabel: {
    fontSize: '17px',
    color: '#666',
    fontWeight: 'bold'
  },
  mainGridContent: {
    width: '100%',
    maxWidth: '1400px',
    margin: '40px auto 80px',
    padding: '0 clamp(16px, 4vw, 20px)',
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    boxSizing: 'border-box'
  },
  mainColumn: {
    flex: '1 1 300px',
    minWidth: 0,
    maxWidth: '100%'
  },
  sidebarColumn: {
    flex: '1 1 280px',
    minWidth: 0,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px'
  },
  cardSection: {
    marginBottom: '60px'
  },
  premiumTitle: {
    fontSize: '32px',
    fontWeight: '900',
    color: '#1a1a1a',
    margin: 0,
    position: 'relative',
    paddingLeft: '15px'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: '35px',
    borderBottom: '2px solid #f0f0f0',
    paddingBottom: '15px'
  },
  viewAllBtn: {
    color: '#c41e3a',
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '15px'
  },
  sidebarCard: {
    background: 'white',
    padding: '30px',
    borderRadius: '20px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
    border: '1px solid #f0f0f0'
  },
  sidebarTitle: {
    fontSize: '20px',
    fontWeight: '900',
    color: '#1a1a1a',
    margin: 0
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    borderBottom: '1px solid #eee',
    paddingBottom: '15px'
  },
  sidebarLink: {
    color: '#c41e3a',
    fontSize: '14px',
    textDecoration: 'none',
    fontWeight: 'bold'
  },
  miniStandings: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  miniRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    background: '#fafafa',
    borderRadius: '10px',
    transition: 'all 0.2s'
  },
  rank: {
    width: '25px',
    fontWeight: '900',
    color: '#888'
  },
  miniLogo: {
    width: '30px',
    height: '30px',
    objectFit: 'contain',
    marginRight: '8px'
  },
  roundSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  roundsListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  roundLabelHome: {
    fontSize: '28px',
    fontWeight: '900',
    color: '#1a1a1a',
    margin: '40px 0 25px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    borderBottom: '4px solid #c41e3a',
    paddingBottom: '12px',
    width: 'fit-content'
  },
  viewAllBtnLarge: {
    padding: '15px 40px',
    background: '#1a1a1a',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '10px',
    fontWeight: 'bold',
    fontSize: '18px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    display: 'inline-block'
  },
  loadMoreBtn: {
    padding: '12px 30px',
    background: 'white',
    color: '#c41e3a',
    border: '2px solid #c41e3a',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.3s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    boxShadow: '0 4px 10px rgba(196, 30, 58, 0.1)'
  },
  miniTeamName: {
    flex: 1,
    fontWeight: 'bold',
    fontSize: '14px'
  },
  miniPoints: {
    background: '#fde8eb',
    color: '#c41e3a',
    padding: '2px 8px',
    borderRadius: '5px',
    fontSize: '13px',
    fontWeight: '900'
  },
  upcomingVList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginTop: '20px'
  },
  vMatchItem: {
    borderLeft: '4px solid #c41e3a',
    padding: '12px 15px',
    background: '#f9f9f9',
    borderRadius: '0 10px 10px 0'
  },
  vMatchTime: {
    fontSize: '12px',
    color: '#c41e3a',
    fontWeight: 'bold',
    marginBottom: '5px'
  },
  vMatchTeams: {
    fontWeight: 'bold',
    fontSize: '15px',
    display: 'flex',
    gap: '8px'
  },
  vMatchVs: {
    color: '#ccc'
  },
  tilesSection: {
    padding: '60px 20px',
    maxWidth: '1300px',
    margin: '0 auto'
  },
  tilesHeader: {
    textAlign: 'center',
    marginBottom: '40px'
  },
  tilesSubtitle: {
    color: '#666',
    marginTop: '10px',
    fontSize: '18px'
  },
  tilesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))',
    gap: '16px',
    width: '100%'
  },
  infoTile: {
    padding: '30px 20px',
    borderRadius: '20px',
    textDecoration: 'none',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: '15px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden'
  },
  tileIconBg: {
    width: '60px',
    height: '60px',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(5px)'
  },
  tileIcon: {
    fontSize: '30px'
  },
  tileLabel: {
    fontSize: '18px',
    fontWeight: 'bold',
    margin: 0
  },
  tileArrow: {
    opacity: 0.7,
    transition: 'transform 0.3s ease'
  },
  valuesSection: {
    padding: '80px 20px',
    background: 'white'
  },
  valuesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '30px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  valueCard: {
    padding: '40px',
    textAlign: 'center',
    borderRadius: '25px',
    background: '#fcfcfc',
    border: '1px solid #eee',
    transition: 'all 0.4s ease'
  },
  valueIcon: {
    fontSize: '40px',
    marginBottom: '20px'
  },
  valueTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    marginBottom: '15px',
    color: '#1a1a1a'
  },
  valueDesc: {
    fontSize: '15px',
    color: '#666',
    lineHeight: '1.7',
    margin: 0
  },
  tickerWrapper: {
    background: '#1a1a1a',
    color: 'white',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 100
  },
  tickerLabel: {
    background: '#c41e3a',
    padding: '0 15px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '14px',
    zIndex: 2,
    boxShadow: '5px 0 15px rgba(0,0,0,0.3)'
  },
  tickerScroll: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    animation: 'ticker 30s linear infinite',
    gap: '50px',
    paddingLeft: '20px'
  },
  tickerItem: {
    fontSize: '14px',
    fontWeight: '500'
  },
  newsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginTop: '20px'
  },
  newsCard: {
    background: 'white',
    borderRadius: '15px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column'
  },
  newsImageContainer: {
    height: '180px',
    position: 'relative',
    overflow: 'hidden'
  },
  newsImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease'
  },
  newsDateBadge: {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    background: 'rgba(196, 30, 58, 0.9)',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 'bold'
  },
  newsContent: {
    padding: '15px'
  },
  newsTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    margin: '0 0 10px 0',
    color: '#1a1a1a'
  },
  newsSnippet: {
    fontSize: '13px',
    color: '#666',
    margin: 0,
    lineHeight: '1.5'
  },
  partnersSection: {
    padding: '60px 0',
    background: '#f8f8f8',
    borderTop: '1px solid #eee'
  },
  containerWidth: {
    maxWidth: '1300px',
    margin: '0 auto',
    padding: '0 20px'
  },
  partnersTitle: {
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '40px'
  },
  partnersGrid: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '40px',
    flexWrap: 'wrap'
  },
  partnerLogoBox: {
    height: '80px',
    width: '150px',
    background: 'white',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
    filter: 'grayscale(100%)',
    opacity: 0.7,
    transition: 'all 0.3s'
  },
  newsletterSection: {
    padding: '80px 20px',
    maxWidth: '1300px',
    margin: '0 auto'
  },
  newsletterCard: {
    background: 'linear-gradient(135deg, #1a1a1a, #333)',
    borderRadius: '30px',
    padding: '40px 60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '40px',
    flexWrap: 'wrap',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
  },
  newsletterInfo: {
    flex: 1,
    minWidth: '300px'
  },
  newsletterTitle: {
    color: 'white',
    fontSize: '32px',
    fontWeight: '800',
    margin: '0 0 10px 0'
  },
  newsletterDesc: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '16px',
    margin: 0
  },
  newsletterForm: {
    display: 'flex',
    gap: '10px',
    flex: 1,
    maxWidth: '500px',
    minWidth: '300px'
  },
  newsletterInput: {
    flex: 1,
    padding: '15px 20px',
    borderRadius: '12px',
    border: 'none',
    background: 'rgba(255,255,255,0.1)',
    color: 'white',
    fontSize: '16px',
    outline: 'none'
  },
  newsletterBtn: {
    padding: '0 30px',
    background: '#c41e3a',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.3s'
  }
};

// Global Enhanced Animations
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;500;600;700;800;900&display=swap');
    
    @keyframes scrollLogos {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    
    @keyframes fadeInScale {
      0% { opacity: 0; transform: scale(1.1); }
      100% { opacity: 1; transform: scale(1); }
    }
    
    @keyframes fadeInUp {
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes pulse {
      0% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.5); opacity: 0.7; }
      100% { transform: scale(1); opacity: 1; }
    }
    
    .btnPrimary:hover, .btnLive:hover {
      transform: translateY(-3px);
      box-shadow: 0 20px 40px rgba(196, 30, 58, 0.5);
    }
    
    .btnSecondary:hover {
      background: rgba(255,255,255,0.2);
    }
    
    .miniRow:hover {
      background: #f0f0f0;
      transform: translateX(-5px);
    }
    
    .featuredLogo:hover {
      transform: scale(1.2) rotate(5deg);
    }

    a[style*="background: linear-gradient"]:hover div:last-child {
      transform: translateX(5px);
    }

    .media-preview-img:hover {
      transform: scale(1.1);
    }

    div:has(.media-preview-img):hover .media-preview-overlay {
      opacity: 1 !important;
    }
    @keyframes ticker {
      0% { transform: translateX(100%); }
      100% { transform: translateX(-100%); }
    }

    .ticker-scroll:hover {
      animation-play-state: paused;
    }

    .newsCard:hover {
      transform: translateY(-10px);
      box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
    }

    .newsCard:hover img {
      transform: scale(1.1);
    }

    .partnerLogoBox:hover {
      filter: grayscale(0%);
      opacity: 1;
      transform: scale(1.1);
    }

    .newsletterBtn:hover {
      background: #e61e3a;
      transform: scale(1.05);
    }

    .valueCard:hover {
      transform: translateY(-10px);
      box-shadow: 0 20px 40px rgba(0,0,0,0.05);
      border-color: #c41e3a;
    }

    @media (max-width: 992px) {
      .home-hero-height { height: 600px !important; }
      .home-main-grid { grid-template-columns: 1fr !important; }
      .newsletterCard { padding: 30px !important; text-align: center; }
      .newsletterForm { max-width: 100% !important; }
    }

    @media (max-width: 768px) {
      .home-hero-height { height: 500px !important; }
      .home-slide-title { fontSize: 32px !important; }
      .home-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
      .tickerLabel { display: none; }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default HomePage;
